import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Emergent Managed Google Auth · callback landing page.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Flow:
 *   1. User clicks "Sign in with Google" on /brand-os/login — we redirect
 *      to `https://auth.emergentagent.com/?redirect=<origin>/brand-os/auth/callback`.
 *   2. After Google, the user lands here with `#session_id=<opaque>` in
 *      the URL fragment.
 *   3. We read the fragment synchronously, POST it to our backend
 *      `/api/brand-os/genesis/auth/google/callback`, and the backend
 *      exchanges the id with Emergent's `/session-data` endpoint,
 *      creates/updates the user, and issues our own `bos_session`
 *      cookie.
 *   4. We refresh the auth context and land the user on the dashboard.
 */
export default function GoogleAuthCallback() {
  const { refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const processed = useRef(false);
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("Finishing your Google sign-in…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    // Read the fragment via useLocation so we react to hash changes
    // (window.location.hash is not reactive here).
    const hash = (location.hash || "").replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const sessionId = (params.get("session_id") || "").trim();

    if (!sessionId) {
      setStatus("error");
      setMessage(
        "No session id was returned by Google. Try signing in again."
      );
      return;
    }

    (async () => {
      try {
        await api.post("/genesis/auth/google/callback", {
          session_id: sessionId,
        });
        // Clear the fragment so a browser refresh doesn't re-run the
        // exchange with a now-consumed id.
        try {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
          );
        } catch (_) {
          /* no-op */
        }
        await refresh();
        setStatus("success");
        setMessage("Signed in with Google. Taking you to your workspace…");
        setTimeout(() => navigate("/brand-os", { replace: true }), 700);
      } catch (err) {
        setStatus("error");
        setMessage(formatApiError(err));
      }
    })();
  }, [location.hash, navigate, refresh]);

  return (
    <div className="bo-root" data-testid="google-auth-callback">
      <div className="bo-login-shell">
        <div className="bo-login-card" style={{ textAlign: "left" }}>
          <div className="bo-login-eyebrow" data-testid="google-auth-eyebrow">
            Precipice Brand OS · Google Sign-in
          </div>
          <h1
            className="bo-login-title"
            style={{ fontSize: 26 }}
            data-testid="google-auth-title"
          >
            {status === "verifying" && "One moment…"}
            {status === "success" && "You're in."}
            {status === "error" && "Google sign-in didn't complete."}
          </h1>
          <p
            className="bo-login-lede"
            style={{ marginTop: 8 }}
            data-testid="google-auth-message"
          >
            {message}
          </p>

          {status === "verifying" && (
            <div
              style={{ marginTop: 24, textAlign: "center" }}
              data-testid="google-auth-spinner"
            >
              <div
                aria-hidden
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "3px solid var(--bo-panel-alt, #e6e2d8)",
                  borderTopColor: "var(--bo-ink, #2b2c30)",
                  margin: "0 auto",
                  animation: "bo-spin 800ms linear infinite",
                }}
              />
              <style>{`@keyframes bo-spin { to { transform: rotate(360deg);} }`}</style>
            </div>
          )}

          {status === "error" && (
            <div style={{ marginTop: 20, display: "flex", gap: 10 }}>
              <button
                type="button"
                className="bo-btn bo-btn-primary"
                onClick={() => navigate("/brand-os/login", { replace: true })}
                data-testid="google-auth-return-to-login"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
